import React, { useState, useCallback, useRef } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import ZoneOverlay from '../DroneZoneMapOverlay/ZoneOverlay';
import ControlPanel from '../DroneZoneMapOverlay/ControlPanel';
import Legend from '../DroneZoneMapOverlay/Legend';
import ExportButton from './ExportButton';
import { OperationalZones } from '../../types/calculator';
import { generateKML, saveKMLFile } from '../../utils/kmlExport';
import '../../styles/DroneZoneMap.css';

interface MapContainerProps {
  zones: OperationalZones;
  apiKey: string;
}

// Default center coordinates (updated as requested)
const defaultCenter = {
  lat: 33.328391,
  lng: -111.962224
};

// Map styling and configuration
const mapContainerStyle = {
  width: '100%',
  height: '500px'
};

const mapOptions = {
  mapTypeId: 'satellite' as google.maps.MapTypeId,
  zoom: 18.5,
  center: defaultCenter,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
  tilt: 0, // Force 2D view (no tilt)
  disableDefaultUI: false,
  rotateControl: false, // Disable rotation control
  heading: 0 // Force north up orientation
};

const MapContainer: React.FC<MapContainerProps> = ({ zones, apiKey }) => {
  // Debug API key length and presence
  console.log('MapContainer - API Key length:', apiKey ? apiKey.length : 0);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: 'drone-zone-map'
  });
  
  // Debug loading state
  console.log('MapContainer - isLoaded:', isLoaded, 'loadError:', !!loadError);

  const [center, setCenter] = useState(defaultCenter);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [address, setAddress] = useState<string>('');
  
  // Reference to maintain map instance
  const mapRef = useRef<google.maps.Map | null>(null);

  // Handle map load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMap(map);
  }, []);

  // Handle map click to set new center
  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const newCenter = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };
      setCenter(newCenter);
    }
  }, []);

  // Track if center change comes from dragging
  const [isDragging, setIsDragging] = useState(false);

  // Handle center change from control panel or dragging
  const handleCenterChange = useCallback((newCenter: { lat: number; lng: number }, panToNewCenter: boolean = true) => {
    // Update component state to track if this update came from dragging
    setIsDragging(!panToNewCenter);
    setCenter(newCenter);
    
    // Only pan the map if explicitly requested (from control panel but not from dragging)
    if (mapRef.current && panToNewCenter) {
      mapRef.current.panTo(newCenter);
    }
  }, []);

  // Handle address search
  const handleAddressSearch = useCallback((newAddress: string) => {
    setAddress(newAddress);
  }, []);

  // Handle KML export
  const handleExportKML = useCallback(() => {
    // Generate the KML content
    const kmlContent = generateKML(zones, center);
    
    // Get current date for filename
    const date = new Date();
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const filename = `drone_zones_${dateStr}.kml`;
    
    // Save the KML file
    saveKMLFile(kmlContent, filename);
  }, [zones, center]);

  if (loadError) {
    return <div className="map-error">Error loading Google Maps: {loadError.message}</div>;
  }

  if (!isLoaded) {
    return <div className="map-loading">Loading Google Maps...</div>;
  }

  return (
    <div className="drone-zone-map-container">
      <div className="location-input-container">
        <ControlPanel 
          center={center} 
          onCenterChange={handleCenterChange} 
          address={address}
          onAddressSearch={handleAddressSearch}
        />
      </div>
      <div className="drone-zone-map-wrapper">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          options={mapOptions}
          center={isDragging ? undefined : center}
          onClick={handleMapClick}
          onLoad={onMapLoad}
          mapContainerClassName="google-map-container"
        >
          {map && (
            <ZoneOverlay 
              map={map} 
              center={center} 
              zones={zones} 
              onCenterChange={handleCenterChange}
            />
          )}
        </GoogleMap>
        
        {/* Export KML button */}
        <div className="map-controls-container">
          <ExportButton onExport={handleExportKML} />
        </div>
      </div>
      <div className="legend-container">
        <Legend />
      </div>
    </div>
  );
};

export default MapContainer;
